import * as React from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { Chat } from "@converge-collective/common/models/Chat";
import { WithID } from "@converge-collective/common/models/Base";
import { formatRelative } from "date-fns";
import { useUser } from "reactfire";
import { chatHashUrl } from "~/lib/chat";
import { isEmpty } from "lodash";
import { Stack } from "@mui/system";
import { useHash } from "react-use";

export default function ChatList({
  chats,
  selectedId,
}: {
  chats: WithID<Chat>[];
  selectedId?: string;
}): React.ReactElement {
  const [, setHash] = useHash();
  const { data: user } = useUser();
  const others = (chat: Chat) => chat.members.filter((m) => m.id !== user?.uid);

  const truncateRecentMessage = (message?: string) => {
    if (!message) return;
    if (message.length > 50) {
      return message.substring(0, 50) + "...";
    } else {
      return message;
    }
  };

  if (isEmpty(chats)) {
    return (
      <Stack
        sx={{ height: "100%" }}
        justifyContent="center"
        alignItems="center"
      >
        <Typography sx={{ color: "grey.600", textAlign: "center" }}>
          Your chats are empty. Start chatting with someone!
        </Typography>
      </Stack>
    );
  }

  return (
    <List
      sx={{
        pt: 0,
        width: "100%",

        // selected and (selected + hover) states
        "&& .Mui-selected, && .Mui-selected:hover": {
          bgcolor: "pallette.secondary.light",
        },
      }}
    >
      {chats.map((chat) => (
        <React.Fragment key={chat.id}>
          <ListItemButton
            onClick={() => setHash(chatHashUrl(chat.id))}
            selected={chat.id === selectedId}
            alignItems="flex-start"
          >
            <ListItemAvatar>
              {others(chat).map((member) => (
                <Avatar
                  key={member.id}
                  alt={member.name}
                  src={member.photoURL}
                />
              ))}
            </ListItemAvatar>
            <ListItemText
              primary={others(chat)
                .map((member) => member.name)
                .join(", ")}
              secondary={
                <React.Fragment>
                  <Typography
                    sx={{ display: "inline" }}
                    component="span"
                    variant="body2"
                    color="text.primary"
                  >
                    {truncateRecentMessage(chat.recentMessages[0]?.body)}
                  </Typography>{" "}
                  {chat.lastActivityAt &&
                    formatRelative(chat.lastActivityAt, new Date())}
                </React.Fragment>
              }
            />
          </ListItemButton>
          <Divider light={true} variant="inset" component="li" />
        </React.Fragment>
      ))}
    </List>
  );
}
